import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";

import { EmptyResultMessage } from "@/components/atoms/EmptyResultMessage";
import { TextFieldForSearch } from "@/components/molecules/SearchTextField";
import Title from "@/components/molecules/Title";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { PartnerUsersConstants } from "./PartnerUsersConstants";
import { UserRoleList } from "./PartnerUsersList";
import {
  PartnerUser,
  PartnerUsersOrder,
  PartnerUsersSortKey,
  PartnerUsersState,
} from "@/models/partnerUsers";
import {
  getPartnerUsersAPI,
  getPartnerUsersCountAPI,
} from "@/util/api/partnerUsers";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import { Office, ONLY_CONTRACT, PLUS_CITY } from "@/models/office";
import { getLoginUserAPI, getOfficesAPI } from "@/util/api/API";
import { GX_ADMIN, UserInfo } from "@/models/auth";
import { AxiosResponse } from "axios";
import Cookies from "js-cookie";
import { MULTI_OFFICE_ID } from "@/common/CommonConstants";
import { AutoCompleteMultiple } from "@/components/molecules/AutoCompleteMultiple";
import { getOfficeOptionLabel } from "@/util";

const PAGE_SIZE = 30;

export const PartnerUsers = (): JSX.Element => {
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [partnerUsers, setPartnerUsers] = useState<PartnerUser[]>([]);

  const [offices, setOffices] = useState<Office[]>([]);
  const [officesSelected, setOfficesSelected] = useState<Office[]>([]);

  const [ownerUserId, setOwnerUserId] = useState<number>();
  const [name, setName] = useState("");
  const [order, setOrder] = useState<PartnerUsersOrder>({
    partner_role_id: "desc",
    name: "asc",
  });
  const [emptyResult, setEmptyResult] = useState<boolean>(false);

  const { savedState, navigate } = useStatefulNavigator<PartnerUsersState>();

  // ユーザーリストを更新する
  const updatePartnerUsersList = useCallback(
    (
      page: number,
      order: PartnerUsersOrder,
      officeIds: number[],
      name?: string
    ) => {
      getPartnerUsersAPI(
        page,
        PAGE_SIZE,
        order,
        officeIds,
        name?.length ? name : undefined
      ).then((res) => {
        setPartnerUsers(res.data ?? []);
      });

      getPartnerUsersCountAPI(officeIds, name?.length ? name : undefined).then(
        (res) => {
          setTotal(res.data ?? 0);
          setPage(page);
          setEmptyResult(res.data === 0);
        }
      );
    },
    []
  );

  const handleChangeOffices = useCallback((value: Office[]) => {
    setOfficesSelected(value);
    Cookies.set(MULTI_OFFICE_ID, value.map(({ id }) => id).join(","));
  }, []);

  // 入力された名前に基づいて料金表をフィルタリングする
  const handleClickSearch = useCallback(() => {
    updatePartnerUsersList(
      1,
      order,
      officesSelected.map(({ id }) => id),
      name
    );
  }, [name, officesSelected, order, updatePartnerUsersList]);

  // 検索条件をリセットする
  const handleClickReset = useCallback(() => {
    setOfficesSelected([]);
    setName("");
    setOrder({
      partner_role_id: "desc",
      name: "asc",
    });
  }, []);

  // ページングの変更に基づいて、一覧を更新する
  const handleChangePage = useCallback(
    (page: number) => {
      updatePartnerUsersList(
        page,
        order,
        officesSelected.map(({ id }) => id),
        name
      );
    },
    [name, officesSelected, order, updatePartnerUsersList]
  );

  // ソート情報の変更に基づいて、一覧を更新する
  const handleChangeSort = useCallback(
    (key: PartnerUsersSortKey) => {
      const nextDirection = ["asc", undefined].includes(order[key])
        ? "desc"
        : "asc";

      const nextOrder = {
        [key]: nextDirection,
      };

      updatePartnerUsersList(
        1,
        nextOrder,
        officesSelected.map(({ id }) => id),
        name
      );
      setOrder(nextOrder);
    },
    [name, officesSelected, order, updatePartnerUsersList]
  );

  const handleClickEdit = useCallback(
    (id: number) => {
      navigate(`/partner_users/${id}`, {
        page,
        order,
        officeIds: officesSelected.map(({ id }) => id),
        name,
      });
    },
    [name, navigate, officesSelected, order, page]
  );

  const setUp = useCallback(
    async ({ data }: AxiosResponse<UserInfo>) => {
      setOwnerUserId(data.id);
      let offices: Office[] = [];
      try {
        offices = (
          await getOfficesAPI(
            data.role === GX_ADMIN ? PLUS_CITY : ONLY_CONTRACT
          )
        ).data;
      } catch (e) {
        console.log("===Error occurred while getting offices===", e);
        return;
      }

      setOffices(offices ?? []);

      if (savedState) {
        const savedOffices = offices.filter(
          ({ id }) => savedState.officeIds?.includes(id) ?? false
        );
        setOfficesSelected(savedOffices);
        setName(savedState.name ?? "");
        setPage(savedState.page);
        setOrder(savedState.order);

        updatePartnerUsersList(
          savedState.page,
          savedState.order,
          savedState.officeIds,
          savedState.name
        );
        return;
      }

      const cookieSavedOffices = offices.filter((d) =>
        Cookies.get(MULTI_OFFICE_ID)
          ?.split(",")
          .includes(d?.id.toString() ?? "-1")
      );
      if (cookieSavedOffices.length) {
        setOfficesSelected(cookieSavedOffices);
        updatePartnerUsersList(
          0,
          { partner_role_id: "desc", name: "asc" },
          cookieSavedOffices.map(({ id }) => id),
          undefined
        );
      }
    },
    [savedState, updatePartnerUsersList]
  );

  useEffect(() => {
    getLoginUserAPI()
      .then(setUp)
      .catch((e) =>
        console.log("===Error occurred while getting UserInfo===", e)
      );
  }, [savedState, setUp, updatePartnerUsersList]);

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>
            {PartnerUsersConstants.PartnerUser}
          </Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6} md={6} lg={6.1}>
                  <AutoCompleteMultiple
                    label={PartnerUsersConstants.OfficeName}
                    options={offices}
                    value={officesSelected}
                    forSearch
                    uniqueKey="id"
                    limit={100}
                    getOptionLabel={getOfficeOptionLabel}
                    onChange={handleChangeOffices}
                  />
                </Grid>
                <Grid item xs={3} md={3} lg={3.1}>
                  <TextFieldForSearch
                    label={"事業者ユーザー名"}
                    value={name}
                    setValue={setName}
                    sx={{ width: "100%" }}
                  />
                </Grid>
                <Grid item xs={12} xl={2.8} container justifyContent="flex-end">
                  <CommonButton
                    btnType="Search"
                    onClickBtn={handleClickSearch}
                  />
                  <CommonButton
                    btnType="ResetSearchCriteria"
                    sx={{ ml: 2 }}
                    onClickBtn={handleClickReset}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* データエリア */}
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <UserRoleList
                onChangePage={handleChangePage}
                partnerUsers={partnerUsers}
                ownerUserId={ownerUserId}
                pagingData={{ page, total, size: PAGE_SIZE }}
                onClickChangeSort={handleChangeSort}
                orderTypes={order}
                onClickEdit={handleClickEdit}
              />
              {emptyResult && <EmptyResultMessage />}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
